// @ts-strict-ignore
import { WireContourService } from '@3d-scene/common/util/wire-contour-service';
import { AxesHelper, Box3, Euler, Object3D, Vector3 } from 'three';
import { ArrayUtils } from '@shared/util/array-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUtils } from '@article/util/article-utils';
import { ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementUsageVO, MetaElementVO, TextureAreaUsageVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ContainerService } from '../container.service';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { ReferenceArticleUsageUtils } from '@article/util/reference-article-usage-utils';
import { Dictionary } from 'typescript-collections';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { Connection } from '../connection';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ProjectVO } from '@project/model/vo/project.vo';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { Rectangle3D } from '../../model/rectangle-3d';
import { MetaElementUsageUtils } from '@project/util/meta-element-usage-utils';
import { ArticleUsagePlausiPropertyDef } from '@article/constants/article-usage-plausi-property-def';
import { NumberUtils } from '@shared/util/number-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticlePlausiPropertyDef } from '@article/constants/article-plausi-property-def';
import { GlobalContainer } from '@3d-scene/model3d/global-container';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { OBJRepresentationDataVO, OBJTextureDataVO } from '@shared/vo/representation.vo';
import { ObjExporter } from '@3d-scene/exporters/obj-exporter';
import { ObjInfo } from '@article/model/obj-info';
import { isLayerElement } from '@3d-scene/layer/elements/i-layer-element';
import { ArticleDescriptorVO } from '@project/model/vo/validation-context.vo';
import { Injectable } from '@angular/core';
import { ProjectUserModel } from '@project/model/project-user.model';
import { ConnectionInfoUtils } from './connection-info-utils';
import { ArticleConnectionService } from '../article-connection.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { WireBridgeService } from '@project/components/article/manager/wire-bridge.service';
import { Object3DUtils } from '../../common/util/object-3d-utils';
import { ConfiguratorParameters } from '../../configurator-parameters/configurator-parameters';
import { IsThreeJsType } from '../../common/util/is-threejs-type';
import { ContainerUtils } from '../container.utils';

@Injectable({ providedIn: 'root' })
export class ArticleContainerUtils {
  constructor(
    private containerService: ContainerService,
    private _referenceArticleUsageUtils: ReferenceArticleUsageUtils,
    private compositeArticleInfoUtils: CompositeArticleInfoUtils,
    private _globalContainer: GlobalContainer,
    private _projectUserModel: ProjectUserModel,
    private _articleConnectionUtils: ArticleConnectionService
  ) {}

  /**
   * Checks whether object is relevant and should be included/considered during bounding box calculations
   * @param object object
   * @return true if relevant
   *
   */
  public isObjectRelevant(object: Object3D): boolean {
    return !(
      object instanceof AxesHelper ||
      isLayerElement(object) ||
      object.name === ArticleContainer.WIRE_LINE_NAME ||
      object.name === WireBridgeService.FLAG_OBJECT_NAME ||
      object.name === WireContourService.WIRECONTOUR_NAME ||
      object.name === WireContourService.WIRECONTOUR_PDF_NAME
    );
  }

  public static extractArticleGUIDs(articleContainers: ArticleContainer[]): string[] {
    const result: string[] = [];
    if (ArrayUtils.isEmpty(articleContainers)) {
      return result;
    }
    for (const container of articleContainers) {
      result.push(container.articleGUID);
    }
    return result;
  }

  public static findArticleContainerByGUIDs(guids: string[], source: ArticleContainer[]): ArticleContainer[] {
    const result: ArticleContainer[] = [];
    if (ArrayUtils.isEmpty(guids)) {
      return result;
    }

    for (const container of source) {
      if (guids.indexOf(container.articleGUID) >= 0) {
        result.push(container);
      }
    }
    return result;
  }

  public findArticleContainerByGUID(guid: string, source: ArticleContainer[]): ArticleContainer {
    if (StringUtils.isEmpty(guid) || ArrayUtils.isEmpty(source)) {
      return null;
    }
    for (const container of source) {
      if (container.articleGUID === guid) {
        return container;
      }
    }
    return null;
  }

  /**
   * @return article containers recursively connected to the given container
   */
  public getConnectedArticlesRecursively(parentArticle: ArticleContainer, includeForwardReferenceArticles: boolean = false): ArticleContainer[] {
    const result: ArticleContainer[] = this.containerService.getConnectedArticleContainers(parentArticle.articleUsage);
    for (const child of result) {
      ArrayUtils.mergeArrays(result, this.getConnectedArticlesRecursively(child, false));
    }
    if (includeForwardReferenceArticles) {
      const forwardReferenceContainers: ArticleContainer[] = this._referenceArticleUsageUtils.getForwardReferenceArticleContainers(
        parentArticle.articleUsage
      );
      ArrayUtils.mergeArrays(result, forwardReferenceContainers);
    }
    return result;
  }

  /**
   * @return article containers recursively connected to the given list of containers
   */
  public getAllConnectedArticlesRecursively(parentArticleContainers: ArticleContainer[], includeForwardReferenceArticles: boolean = false): ArticleContainer[] {
    const childArticles: ArticleContainer[] = [];
    for (const parentContainer of parentArticleContainers) {
      ArrayUtils.mergeArrays(childArticles, this.getConnectedArticlesRecursively(parentContainer, includeForwardReferenceArticles));
    }
    return childArticles;
  }

  public isArticleCombined(articleContainer: ArticleContainer): boolean {
    return this.findCombinedArticles(articleContainer, true, null, 0).length > 1;
  }

  /**
   * ATTENTION: there are two methods in flex-version which area called the same way:
   * ArticleContainerUtils.findCombinedArticles(), ArticleContainerUtils.findCombinedArticlesWithEndplates()
   *
   * Returns article containers connected to each other on the same "level".
   *
   * @param articleContainer article container
   * @param includeLabeling if this paremeter is false then articles will not be considered as connected/combined
   * if they are connected only with dynamic label article
   * @param excludedArticleGUIDs guids of article containers to exclude from checking
   *
   * @return array with combined articles
   */
  public findCombinedArticles(
    articleContainer: ArticleContainer,
    includeLabeling: boolean = true,
    excludedArticleGUIDs: string[] = null,
    forceLevel?: number
  ): ArticleContainer[] {
    const combinedArticlesMap: Dictionary<number, ArticleContainer[]> = this.internalEnumerateCombinedArticles(
      articleContainer,
      includeLabeling,
      false,
      null,
      excludedArticleGUIDs,
      new Dictionary<string, number>(),
      forceLevel
    );
    return combinedArticlesMap.getValue(0);
  }

  /**
   * Returns all article containers (including provided container)
   * that are connected to this provided container (via connections or as child articles).
   */
  public enumerateCombinedArticles(articleContainer: ArticleContainer): ArticleContainer[] {
    const combinedArticlesMap: Dictionary<number, ArticleContainer[]> = this.internalEnumerateCombinedArticles(articleContainer);
    const combinedArticles: ArticleContainer[] = [];
    for (const level of combinedArticlesMap.keys()) {
      ArrayUtils.mergeArrays(combinedArticles, combinedArticlesMap.getValue(level));
    }
    return combinedArticles;
  }

  public enumerateCombinedArticlesForContainers(articleContainers: ArticleContainer[]): ArticleContainer[] {
    if (!articleContainers || articleContainers.length === 0) {
      return [];
    }
    const result: ArticleContainer[] = [];
    for (const articleContainer of articleContainers) {
      const combinedArticles: ArticleContainer[] = this.enumerateCombinedArticles(articleContainer);
      if (combinedArticles) {
        for (const article of combinedArticles) {
          if (result.indexOf(article) === -1) {
            result.push(article);
          }
        }
      }
    }
    return result;
  }

  public splitCombinedArticlesByLevels(articleContainer: ArticleContainer): Dictionary<number, ArticleContainer[]> {
    return this.internalEnumerateCombinedArticles(articleContainer);
  }

  private fillDistanceFromRailDictionary(
    articleContainer: ArticleContainer, railDistances: Dictionary<string, number>): void {
    if (railDistances.containsKey(articleContainer.articleGUID)) {
      return;
    }

    const parentArticleGUIDs: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleContainer.articleUsage);
    for (const parentGUID of parentArticleGUIDs) {
      const foundParent = this.containerService.findArticleContainerInGlobal(parentGUID);

      // Workaround because of undo of Replace with LTL articles
      // #108843: problem undoing replace articles with Bridges/Jumpers
      if (!foundParent) {
        // In a refresh during undo-replace-articles the backward connections of the bridge article
        // may not be set correctly yet.
        // In that case we skip the calculation of the distance in order to complete the undo operation.
        continue;
      }

      if (ArticleUtils.isMountingRail(foundParent.article) || ArticleUtils.isBusbarArticle(foundParent.article)) {
        railDistances.setValue(parentGUID, 0);
      }
      this.fillDistanceFromRailDictionary(foundParent, railDistances);
      railDistances.setValue(
        articleContainer.articleGUID,
        Math.max(
          railDistances.containsKey(articleContainer.articleGUID) ? railDistances.getValue(articleContainer.articleGUID) : 0,
          railDistances.getValue(parentGUID) + 1
        )
      );
    }
  }

  private internalEnumerateCombinedArticles(
    articleContainer: ArticleContainer,
    includeLabeling: boolean = true,
    includeComplexArticlesOnRail: boolean = false,
    result: Dictionary<number, ArticleContainer[]> = null,
    excludedArticleGUIDs: string[] = null,
    railDistances: Dictionary<string, number> = new Dictionary<string, number>(),
    forceLevel?: number
  ): Dictionary<number, ArticleContainer[]> {
    this.fillDistanceFromRailDictionary(articleContainer, railDistances);
    const calculatedLevel = railDistances.containsKey(articleContainer.articleGUID)
      ? railDistances.getValue(articleContainer.articleGUID) - 1 : 0;
    const level = (forceLevel !== undefined)
      ? Math.min(forceLevel, calculatedLevel) : calculatedLevel;
    if (!result) {
      result = new Dictionary<number, ArticleContainer[]>();
    }
    if (!result.containsKey(level)) {
      result.setValue(level, []);
    }
    if (excludedArticleGUIDs && excludedArticleGUIDs.indexOf(articleContainer.articleGUID) !== -1) {
      return result;
    }
    const articlesOnCurrentLevel: ArticleContainer[] = result.getValue(level);
    if (this.findArticleContainerByGUID(articleContainer.articleGUID, articlesOnCurrentLevel)) {
      return result;
    }

    articlesOnCurrentLevel.push(articleContainer);
    let articlesToProcess: ArticleContainer[] = [articleContainer];
    if (
      includeComplexArticlesOnRail &&
      this.compositeArticleInfoUtils.isCompositePartArticle(articleContainer) &&
      this.compositeArticleInfoUtils.isCompositePartArticleStickTogether(articleContainer) &&
      this.compositeArticleInfoUtils.isBaseCompositePartArticle(articleContainer)
    ) {
      const baseComplexArticles: ArticleContainer[] = this.containerService.getBaseCompositePartArticles(articleContainer);
      if (baseComplexArticles.indexOf(articleContainer) !== -1) {
        ArrayUtils.mergeArrays(articlesOnCurrentLevel, baseComplexArticles);
        articlesToProcess = baseComplexArticles;
      }
    }

    this.processConnectedArticles(articlesToProcess, includeLabeling, includeComplexArticlesOnRail, excludedArticleGUIDs, railDistances, level, result);

    return result;
  }

  private processConnectedArticles(
    articlesToProcess: ArticleContainer[],
    includeLabeling: boolean,
    includeComplexArticlesOnRail: boolean,
    excludedArticleGUIDs: string[],
    railDistances: Dictionary<string, number>,
    level: number,
    result: Dictionary<number, ArticleContainer[]>,
  ) {
    for (const articleToProcess of articlesToProcess) {
      this.processChildContainers(articleToProcess, includeLabeling, includeComplexArticlesOnRail, excludedArticleGUIDs, railDistances, level, result);
      this.processParentContainers(articleToProcess, includeLabeling, includeComplexArticlesOnRail, excludedArticleGUIDs, railDistances, level, result);
    }
  }

  private processChildContainers(
    articleToProcess: ArticleContainer,
    includeLabeling: boolean,
    includeComplexArticlesOnRail: boolean,
    excludedArticleGUIDs: string[],
    railDistances: Dictionary<string, number>,
    level: number,
    result: Dictionary<number, ArticleContainer[]>,
  ) {
    const childArticles: ArticleContainer[] = this.getConnectedArticles(articleToProcess);
    for (const childArticle of childArticles) {
      if (!includeLabeling && ArticleUtils.isDynamicLabelingArticle(childArticle.article)) {
        continue;
      }
      this.internalEnumerateCombinedArticles(
        childArticle,
        includeLabeling,
        includeComplexArticlesOnRail,
        result,
        excludedArticleGUIDs,
        railDistances,
        level + 1,
      );
    }
  }

  private processParentContainers(
    articleToProcess: ArticleContainer,
    includeLabeling: boolean,
    includeComplexArticlesOnRail: boolean,
    excludedArticleGUIDs: string[],
    railDistances: Dictionary<string, number>,
    level: number,
    result: Dictionary<number, ArticleContainer[]>,
  ) {
    if (level > 0) {
      const parentArticleGUIDs: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleToProcess.articleUsage);
      for (const parentGUID of parentArticleGUIDs) {
        const foundArticle = this.containerService.findArticleContainerInGlobal(parentGUID);

        // Workaround because of undo of Replace with LTL articles
        if (!foundArticle) {
          continue;
        }

        this.internalEnumerateCombinedArticles(
          foundArticle,
          includeLabeling,
          includeComplexArticlesOnRail,
          result,
          excludedArticleGUIDs,
          railDistances,
          level - 1,
        );
      }
    }
  }

  public getConnectedArticles(articleContainer: ArticleContainer, includeCompositeArticles: boolean = true): ArticleContainer[] {
    const childArticles: ArticleContainer[] = this.containerService.getConnectedArticleContainers(articleContainer.articleUsage);
    if (!includeCompositeArticles) {
      return childArticles;
    }
    for (const childArticle of childArticles.concat()) {
      if (
        this.compositeArticleInfoUtils.isCompositePartArticle(childArticle) &&
        this.compositeArticleInfoUtils.isCompositePartArticleStickTogether(childArticle) &&
        !this.compositeArticleInfoUtils.isPartArticlesOfSameCompositeArticle(articleContainer, childArticle)
      ) {
        const baseArticles: ArticleContainer[] = this.containerService.getBaseCompositePartArticles(childArticle);
        for (const baseArticle of baseArticles) {
          if (!this.findArticleContainerByGUID(baseArticle.articleGUID, childArticles)) {
            childArticles.push(baseArticle);
          }
        }
      }
    }
    return childArticles;
  }

  public static calculateBBox(object: Object3D): Box3 {
    const resultBox = new Box3();
    if (object instanceof AxesHelper || !NumberUtils.isNumber(object.scale.length())) {
      return resultBox;
    }
    let bbox: Box3;
    const children = object.children;
    if (children.length > 0) {
      for (let child of children) {
        bbox = this.calculateBBox(child);
        resultBox.union(bbox);
      }
    } else if (
      (IsThreeJsType.mesh(object) && !MeshUtils.isConnection(object.name))
      || IsThreeJsType.lineSegments(object)
      || object.name == WireContourService.WIRECONTOUR_NAME) {
      bbox = new Box3().setFromObject(object);
      resultBox.union(bbox);
    }
    return resultBox;
  }

  public static calculateBBoxForArticles(articles: readonly ArticleContainer[]): Box3 {
    if (!articles || articles.length === 0) {
      return null;
    }

    const bbox: Box3 = new Box3();
    for (let article of articles) {
      bbox.union(ArticleContainerUtils.calculateBBox(article));
    }
    return bbox;
  }

  /**
   * Applies provided textValue to referenced textureAreaUsage. If successfull returns found original/previous textValue,
   * otherwise returns null.
   * @return found original/previous textValue
   *
   */
  public applyTextToTextureAreaUsage(textureAreaUsageIdentity: MetaElementUsageIdentityVO, textValue: TextValueVO): TextValueVO {
    if (!textureAreaUsageIdentity || !textureAreaUsageIdentity.articleUsageId || !textureAreaUsageIdentity.metaElementName) {
      return null;
    }
    const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(textureAreaUsageIdentity.articleUsageId);
    if (!container) {
      return null;
    }
    const connectionCube: Connection = container.findFirstConnectionByName(textureAreaUsageIdentity.metaElementName);
    if (!connectionCube) {
      return null;
    }
    const textureAreaUsage: TextureAreaUsageVO = connectionCube.metaElementUsage as TextureAreaUsageVO;
    if (!textureAreaUsage) {
      return null;
    }
    const originalTextValue: TextValueVO = textureAreaUsage.textValue;
    textureAreaUsage.textValue = textValue;
    container.refreshTextureConnections();
    return originalTextValue;
  }

  /**
   * @return article containers which aren't connected with other articles from the given array
   */
  public static getBaseArticleContainers(articleContainers: ArticleContainer[]): ArticleContainer[] {
    const baseContainers: ArticleContainer[] = [];
    const usageIds: string[] = this.extractArticleGUIDs(articleContainers);
    for (const container of articleContainers) {
      const parentUsageIds: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(container.articleUsage);
      let parentUsageIncluded: boolean = false;
      for (const parentId of parentUsageIds) {
        if (usageIds.indexOf(parentId) >= 0) {
          parentUsageIncluded = true;
          break;
        }
      }
      if (!parentUsageIncluded) {
        baseContainers.push(container);
      }
    }
    return baseContainers;
  }

  public updateActiveSetInArticleContainers(articleContainers: ArticleContainer[]): void {
    for (const createdArticle of articleContainers) {
      const articleUsage: ArticleUsageVO = createdArticle.articleUsage;
      const givingConnectionIds: MetaElementUsageIdentityVO[] = createdArticle.getCurrentArticleGivingAreaIds();
      // all used giving connections belong to the same connection-set, so determination of connection-set by first found giving
      // connection is sufficient
      const targetGivingMetaElementUsageId: MetaElementUsageIdentityVO = givingConnectionIds.length === 0 ? null : givingConnectionIds[0];
      if (targetGivingMetaElementUsageId != null) {
        const setId: string = ConnectionInfoUtils.getConnectionSetIdByConnectionName(
          createdArticle.article,
          targetGivingMetaElementUsageId.metaElementName
        );
        if (setId) {
          ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.ACTIVE_CONNECTION_SET_ID, setId);
        }
      }
    }
  }

  /**
   * @return true if all given connections have different article containers, otherwise false
   *
   */
  public allConnectionsBelongToDifferentContainers(connections: Connection[]): boolean {
    const guids: string[] = [];
    for (const connectionCube of connections) {
      const container: ArticleContainer = this.containerService.getArticleContainer(connectionCube);
      if (guids.indexOf(container.articleGUID) === -1) {
        guids.push(container.articleGUID);
      } else {
        return false;
      }
    }
    return true;
  }

  public static collectContainersGeometricData(
    object3d: Object3D,
    isObjectContainer3DIncludedInExportFunction: Function,
    usageIdToPositionMap = new Dictionary<string, any>()
  ): Dictionary<string, any> {
    if (isObjectContainer3DIncludedInExportFunction !== null && !isObjectContainer3DIncludedInExportFunction(object3d)) {
      return usageIdToPositionMap;
    }
    let exportChildren = true;
    if (ContainerUtils.isCompositionContainer(object3d)) {
      usageIdToPositionMap.setValue(object3d.compositionGuid, { position: object3d.position });
    } else if (ContainerUtils.isArticleContainer(object3d)) {
      const euler: Euler = object3d.rotation.clone();
      euler.reorder('ZYX');
      usageIdToPositionMap.setValue(object3d.articleGUID, {
        position: object3d.position,
        rotationX: MathUtils.toDegrees(euler.x),
        rotationY: MathUtils.toDegrees(euler.y),
        rotationZ: MathUtils.toDegrees(euler.z),
      });
      exportChildren = false;
    }

    if (exportChildren) {
      for (const child of object3d.children) {
        this.collectContainersGeometricData(child, isObjectContainer3DIncludedInExportFunction, usageIdToPositionMap);
      }
    }

    return usageIdToPositionMap;
  }

  public static updateProjectWithGeometricData(project: ProjectVO, globalContainer: GlobalContainer): void {
    const guidToDataMapping: Dictionary<string, any> = this.collectContainersGeometricData(globalContainer, function (object: Object3D): boolean {
      return !(
        object instanceof Connection ||
        IsThreeJsType.lineSegments(object) ||
        object.name == WireContourService.WIRECONTOUR_NAME ||
        isLayerElement(object) ||
        Object3DUtils.isHiddenInGeometryRepresentation(object)
      );
    });
    for (const composition of project.compositions) {
      let geometricData = guidToDataMapping.getValue(composition.id);
      if (geometricData && geometricData.position) {
        PropertyUtils.setFloatPropertyValue(composition.infoProperties, ArticleUsageInfoPropertyDef.ARTICLE_POSITION_X, -geometricData.position.x);
        PropertyUtils.setFloatPropertyValue(composition.infoProperties, ArticleUsageInfoPropertyDef.ARTICLE_POSITION_Y, geometricData.position.y);
        PropertyUtils.setFloatPropertyValue(composition.infoProperties, ArticleUsageInfoPropertyDef.ARTICLE_POSITION_Z, geometricData.position.z);
      }
      if (composition.articleUsages) {
        for (const articleUsage of composition.articleUsages) {
          geometricData = guidToDataMapping.getValue(articleUsage.id);
          if (geometricData) {
            if (geometricData.position) {
              ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
                articleUsage,
                ArticleUsageInfoPropertyDef.ARTICLE_POSITION_X,
                -geometricData.position.x
              );
              ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
                articleUsage,
                ArticleUsageInfoPropertyDef.ARTICLE_POSITION_Y,
                geometricData.position.y
              );
              ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
                articleUsage,
                ArticleUsageInfoPropertyDef.ARTICLE_POSITION_Z,
                geometricData.position.z
              );
            }

            if (NumberUtils.isNumber(geometricData.rotationX)) {
              ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_ROTATION_X, geometricData.rotationX);
            }
            if (NumberUtils.isNumber(geometricData.rotationY)) {
              ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
                articleUsage,
                ArticleUsageInfoPropertyDef.ARTICLE_ROTATION_Y,
                -geometricData.rotationY
              );
            }
            if (NumberUtils.isNumber(geometricData.rotationZ)) {
              ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
                articleUsage,
                ArticleUsageInfoPropertyDef.ARTICLE_ROTATION_Z,
                -geometricData.rotationZ
              );
            }
          }
        }
      }
    }
  }

  /**
   * Applies left-handed transformation specified in article-usage-info properties to a given article container.
   */
  public static applyTransformationToContainerByUsageProperties(articleContainer: ArticleContainer): void {
    if (!articleContainer) {
      return;
    }

    const xrotation: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
      articleContainer.articleUsage,
      ArticleUsageInfoPropertyDef.ARTICLE_ROTATION_X
    );
    const yrotation: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
      articleContainer.articleUsage,
      ArticleUsageInfoPropertyDef.ARTICLE_ROTATION_Y
    );
    const zrotation: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
      articleContainer.articleUsage,
      ArticleUsageInfoPropertyDef.ARTICLE_ROTATION_Z
    );

    // apply left-handed rotation specified in flex-version
    articleContainer.rotateToInReversedOrder(xrotation, -yrotation, -zrotation);

    const xposition: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
      articleContainer.articleUsage,
      ArticleUsageInfoPropertyDef.ARTICLE_POSITION_X
    );
    const yposition: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
      articleContainer.articleUsage,
      ArticleUsageInfoPropertyDef.ARTICLE_POSITION_Y
    );
    const zposition: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
      articleContainer.articleUsage,
      ArticleUsageInfoPropertyDef.ARTICLE_POSITION_Z
    );

    // apply left-handed position specified in flex-version (move in -x direction)
    articleContainer.moveTo(-xposition, yposition, zposition);
  }

  public static findArticleContainerByUsageInfoPropertyStringValue(
    propertyName: string,
    propertyValue: string,
    source: ArticleContainer[]
  ): ArticleContainer {
    for (const container of source) {
      if (ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(container.articleUsage, propertyName) === propertyValue) {
        return container;
      }
    }
  }
  public createObjRepresentationData(
    exportMaterials: boolean,
    exportWireContour: boolean,
    exportMeshes: boolean,
    exportBoundingBoxes: boolean,
    exportArticleTextures: boolean,
    exportInverseTextures: boolean,
    useShortNames: boolean = false
  ): OBJRepresentationDataVO {
    return this.internalCreateObjRepresentationData(
      null,
      exportMaterials,
      exportWireContour,
      exportMeshes,
      exportBoundingBoxes,
      exportArticleTextures,
      exportInverseTextures,
      useShortNames
    );
  }

  public scheduleCreateObjRepresentationData(
    resultHandler: Function,
    exportMaterials: boolean,
    exportWireContour: boolean,
    exportMeshes: boolean,
    exportBoundingBoxes: boolean,
    exportArticleTextures: boolean,
    exportInverseTextures: boolean,
    useShortNames: boolean = false
  ): void {
    this.internalCreateObjRepresentationData(
      resultHandler,
      exportMaterials,
      exportWireContour,
      exportMeshes,
      exportBoundingBoxes,
      exportArticleTextures,
      exportInverseTextures,
      useShortNames
    );
  }

  private internalCreateObjRepresentationData(
    resultHandler: Function,
    exportMaterials: boolean,
    exportWireContour: boolean,
    exportMeshes: boolean,
    exportBoundingBoxes: boolean,
    exportArticleTextures: boolean,
    exportInverseTextures: boolean,
    useShortNames: boolean = false
  ): OBJRepresentationDataVO {
    const exporter: ObjExporter = new ObjExporter(this._projectUserModel);
    const excludedMetaElementsMap: Dictionary<string, string[]> = this.collectExcludedMetaElements();
    const filterObjectsFunction: Function = (object: Object3D): boolean => {
      return !(
        object instanceof Connection ||
        object instanceof AxesHelper ||
        isLayerElement(object) ||
        (!exportWireContour && object.name === WireContourService.WIRECONTOUR_NAME) ||
        ArticleContainerUtils.isExcludedMetaElement(object, excludedMetaElementsMap) ||
        Object3DUtils.isHiddenInGeometryRepresentation(object)
      );
    };
    if (!resultHandler) {
      const globalContainerData: ObjInfo = exporter.export(
        this._globalContainer,
        exportMeshes,
        exportMaterials,
        exportBoundingBoxes,
        true,
        exportArticleTextures,
        exportInverseTextures,
        useShortNames,
        filterObjectsFunction
      );
      return ArticleContainerUtils.constructObjRepresentationData(globalContainerData);
    } else {
      exporter.scheduleExport(
        (globalContainerData: ObjInfo): void => resultHandler(ArticleContainerUtils.constructObjRepresentationData(globalContainerData)),
        this._globalContainer,
        exportMeshes,
        exportMaterials,
        exportBoundingBoxes,
        true,
        exportArticleTextures,
        exportInverseTextures,
        useShortNames,
        filterObjectsFunction
      );
      return null;
    }
  }

  /**
   * Exports current project by parameters specified in <code>ConfiguratorParametersProvider</code>.
   */
  public createConfiguredObjRepresentationData(
    exportMaterials: boolean,
    exportWireContour: boolean,
    exportMeshes: boolean,
    exportBoundingBoxes: boolean,
    exportArticleTextures: boolean
  ): OBJRepresentationDataVO {
    return this.createObjRepresentationData(
      exportMaterials,
      exportWireContour,
      exportMeshes,
      exportBoundingBoxes,
      exportArticleTextures,
      ConfiguratorParameters.inverseTextureCoordinates,
      false
    );
  }

  public scheduleCreateConfiguredObjRepresentationData(
    resultHandler: Function,
    exportMaterials: boolean,
    exportWireContour: boolean,
    exportMeshes: boolean,
    exportBoundingBoxes: boolean,
    exportArticleTextures: boolean
  ): void {
    this.scheduleCreateObjRepresentationData(
      resultHandler,
      exportMaterials,
      exportWireContour,
      exportMeshes,
      exportBoundingBoxes,
      exportArticleTextures,
      ConfiguratorParameters.inverseTextureCoordinates,
      false
    );
  }

  /**
   * @return representation data of specific articles in current project
   */
  public createObjRepresentationDataForArticles(
    articleGUIDs: string[],
    exportMaterials: boolean,
    exportWireContour: boolean,
    exportMeshes: boolean,
    exportBoundingBoxes: boolean,
    exportArticleTextures: boolean,
    exportInverseTextures: boolean,
    useShortNames: boolean = false
  ): OBJRepresentationDataVO {
    const exporter: ObjExporter = new ObjExporter(this._projectUserModel);
    const excludedMetaElementsMap: Dictionary<string, string[]> = this.collectExcludedMetaElements();
    const globalContainerData: ObjInfo = exporter.export(
      this._globalContainer,
      exportMeshes,
      exportMaterials,
      exportBoundingBoxes,
      true,
      exportArticleTextures,
      exportInverseTextures,
      useShortNames,
      (object: Object3D): boolean => {
        return (
          !(
            object instanceof Connection ||
            object instanceof AxesHelper ||
            isLayerElement(object) ||
            ArticleContainerUtils.isExcludedMetaElement(object, excludedMetaElementsMap)
          ) &&
          (object === this._globalContainer ||
            ContainerUtils.isCompositionContainer(object) ||
            (ContainerUtils.isArticleContainer(object) &&
              ArticleContainerUtils.isArticleContainerInArticleGUIDs(object, articleGUIDs)) ||
            (object instanceof Object3D &&
              ArticleContainerUtils.isArticleContainerInArticleGUIDs(ArticleContainerUtils.getParentArticleContainer(object), articleGUIDs)))
        );
      }
    );
    return ArticleContainerUtils.constructObjRepresentationData(globalContainerData);
  }

  private static constructObjRepresentationData(objInfo: ObjInfo): OBJRepresentationDataVO {
    if (!objInfo) {
      return null;
    }
    const objRepresentationDataVO: OBJRepresentationDataVO = new OBJRepresentationDataVO();
    objRepresentationDataVO.textures = [];
    objRepresentationDataVO.compressed = true;
    objRepresentationDataVO.encoded = true;

    objRepresentationDataVO.modelDataString = StringUtils.compressString(objInfo.objContent);
    objRepresentationDataVO.materialLibraryDataString = StringUtils.compressString(objInfo.mtlContent);

    if (!ArrayUtils.isEmpty(objInfo.textures)) {
      for (const textureInfo of objInfo.textures) {
        const texture: OBJTextureDataVO = new OBJTextureDataVO();
        texture.name = textureInfo.name;
        texture.content = textureInfo.content;
        texture.url = textureInfo.url;
        texture.articleParameters = textureInfo.articleParameters;
        objRepresentationDataVO.textures.push(texture);
      }
    }
    return objRepresentationDataVO;
  }

  public collectExcludedMetaElements(): Dictionary<string, string[]> {
    const excludedMetaElementsMap: Dictionary<string, string[]> = new Dictionary<string, string[]>();
    for (const articleContainer of this.containerService.enumerateArticleContainersInGlobal()) {
      if (!articleContainer.articleUsage || !articleContainer.articleUsage.metaElementUsages) {
        continue;
      }

      const excludedMetaElements: string[] = [];
      for (const textureAreaUsage of ArticleUsageUtils.textureAreaUsages(articleContainer.articleUsage)) {
        if (textureAreaUsage.previousMergedUsageId) {
          excludedMetaElements.push(textureAreaUsage.identity.metaElementName);
        }
      }

      if (excludedMetaElements.length > 0) {
        excludedMetaElementsMap.setValue(articleContainer.articleGUID, excludedMetaElements);
      }
    }

    return excludedMetaElementsMap;
  }

  public static isExcludedMetaElement(object: Object3D, excludedMetaElementsMap: Dictionary<string, string[]>): boolean {
    if (!(IsThreeJsType.mesh(object) && MeshUtils.isConnection(object.name))) {
      return false;
    }

    const parentArticleContainer: ArticleContainer = this.getParentArticleContainer(object);
    if (!parentArticleContainer) {
      return false;
    }

    const articleContainerGUID: string = parentArticleContainer.articleGUID;
    if (excludedMetaElementsMap.containsKey(articleContainerGUID)) {
      const excludedMetaElements = excludedMetaElementsMap.getValue(articleContainerGUID);
      if (excludedMetaElements.indexOf(object.name) !== -1) {
        return true;
      }
    }

    return false;
  }

  private static getParentArticleContainer(objectContainer: Object3D): ArticleContainer {
    if (!objectContainer) {
      return null;
    }
    return objectContainer.parent && ContainerUtils.isArticleContainer(objectContainer.parent)
      ? objectContainer.parent
      : this.getParentArticleContainer(objectContainer.parent);
  }

  private static isArticleContainerInArticleGUIDs(articleContainer: ArticleContainer, articleGUIDs: string[]): boolean {
    if (!articleContainer || ArrayUtils.isEmpty(articleGUIDs)) {
      return false;
    }
    return articleGUIDs.indexOf(articleContainer.articleGUID) !== -1;
  }

  public getArticleContainersByArticleDescriptors(articleDescriptors: ArticleDescriptorVO[]): ArticleContainer[] {
    if (ArrayUtils.isEmpty(articleDescriptors)) {
      return [];
    }
    const articleUsageIds: string[] = ArticleUsageUtils.getArticleUsageIdsByArticleDescriptors(articleDescriptors);
    if (ArrayUtils.isEmpty(articleUsageIds)) {
      return [];
    }
    return this.containerService.findArticleContainersInGlobalByGuids(articleUsageIds);
  }

  public findCombinedArticlesWithEndplates(
    articleContainer: ArticleContainer,
    includeLabeling: boolean = true,
    includeComplexArticlesOnRail: boolean = false
  ): ArticleContainer[] {
    const combinedArticlesMap: Dictionary<number, ArticleContainer[]> = this.enumerateCombinedArticlesWithEndPlates(
      articleContainer,
      includeLabeling,
      includeComplexArticlesOnRail
    );
    return combinedArticlesMap.getValue(0);
  }

  public enumerateCombinedArticlesInclEndplates(
    articleContainer: ArticleContainer,
    includeComplexArticlesOnRail: boolean = false
  ): ArticleContainer[] {
    const combinedArticlesMap: Dictionary<number, ArticleContainer[]> = this.enumerateCombinedArticlesWithEndPlates(
      articleContainer,
      true,
      includeComplexArticlesOnRail
    );
    const combinedArticles: ArticleContainer[] = [];
    for (const level of combinedArticlesMap.keys()) {
      ArrayUtils.mergeArrays(combinedArticles, combinedArticlesMap.getValue(level));
    }
    return combinedArticles;
  }

  private enumerateCombinedArticlesWithEndPlates(
    articleContainer: ArticleContainer,
    includeLabeling: boolean = true,
    includeComplexArticlesOnRail: boolean = false
  ): Dictionary<number, ArticleContainer[]> {
    const combinedArticlesMap: Dictionary<number, ArticleContainer[]> = this.internalEnumerateCombinedArticles(
      articleContainer,
      includeLabeling,
      includeComplexArticlesOnRail,
    );
    const baseArticles: ArticleContainer[] = combinedArticlesMap.getValue(0);
    const childLevel: number = 1;
    if (includeLabeling && baseArticles.length > 1 && combinedArticlesMap.containsKey(childLevel)) {
      for (const childContainer of combinedArticlesMap.getValue(childLevel)) {
        if (!ArticleUtils.isDynamicLabelingArticle(childContainer.article)) {
          continue;
        }
        const parentUsageIds: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(childContainer.articleUsage);
        const parentContainers: ArticleContainer[] = ArticleContainerUtils.findArticleContainerByGUIDs(parentUsageIds, baseArticles);
        const endPlateContainers: ArticleContainer[] = this.findEndPlatesBetweenArticles(
          parentContainers,
          childContainer.createLabelingHelper.getInsertArticleHelper()
        );
        ArrayUtils.mergeArrays(baseArticles, endPlateContainers);
      }
    }
    return combinedArticlesMap;
  }

  private findEndPlatesBetweenArticles(articleContainers: ArticleContainer[], insertHelper: InsertArticleHelper): ArticleContainer[] {
    if (!insertHelper) {
      return [];
    }
    let articlesOnMountingRail: ArticleContainer[] = this.filterArticlesConnectedToMountingRail(articleContainers);
    const endPlateArticles: ArticleContainer[] = [];
    if (articlesOnMountingRail.length > 1) {
      articlesOnMountingRail = insertHelper.sortArticles(articlesOnMountingRail);
      const articlesBetween: ArticleContainer[] = insertHelper.getConnectedArticlesBetweenContainers(
        articlesOnMountingRail[0],
        articlesOnMountingRail[articlesOnMountingRail.length - 1],
        ArticleContainerUtils.extractArticleGUIDs(articlesOnMountingRail)
      );

      for (const container of articlesBetween) {
        if (ArticleUtils.isEndPlateArticle(container.article)) {
          endPlateArticles.push(container);
        }
      }
    }
    return endPlateArticles;
  }

  public static sortArticleContainersByDistanceOfPosition(containers: ArticleContainer[], pointToSort: Vector3): ArticleContainer[] {
    if (!containers || !pointToSort) {
      return [];
    }
    const sortedContainers: ArticleContainer[] = containers.sort((a: ArticleContainer, b: ArticleContainer) => {
      return pointToSort.distanceTo(a.position) - pointToSort.distanceTo(b.position);
    });
    return sortedContainers;
  }

  public static sortArticleContainersByDistanceOfBBox(containers: ArticleContainer[], pointToSort: Vector3): void {
    const calculatedBBoxes: Map<string, Box3> = containers.reduce((map, container) => {
      map.set(container.articleGUID, ArticleContainerUtils.calculateBBox(container));
      return map;
    }, new Map<string, Box3>());

    containers.sort((a: ArticleContainer, b: ArticleContainer) => {
      return calculatedBBoxes.get(a.articleGUID).distanceToPoint(pointToSort) - calculatedBBoxes.get(b.articleGUID).distanceToPoint(pointToSort);
    });
  }

  public getBackwardArticleContainerOnRailRecursively(articleContainer: ArticleContainer): ArticleContainer {
    if (!articleContainer) {
      return null;
    }
    const backwardUsageId: string = ArticleUsageUtils.getFirstBackwardConnectedArticleUsageId(articleContainer.articleUsage);
    const backwardArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(backwardUsageId);
    if (!backwardArticleContainer) {
      return null;
    }
    return ArticleUtils.isMountingRail(backwardArticleContainer.article)
      ? articleContainer
      : this.getBackwardArticleContainerOnRailRecursively(backwardArticleContainer);
  }

  public getAllBackwardArticleContainerOnRailRecursively(articleContainer: ArticleContainer): ArticleContainer[] {
    const result: ArticleContainer[] = [];
    if (!articleContainer) {
      return result;
    }
    const backwardUsageIds: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleContainer.articleUsage);
    const backwardArticleContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(backwardUsageIds);
    if (ArrayUtils.isEmpty(backwardArticleContainers)) {
      return result;
    }
    backwardArticleContainers.forEach((ac) => {
      if (ArticleUtils.isMountingRail(ac.article)) {
        ArrayUtils.mergeArrays(result, [articleContainer]);
      } else {
        ArrayUtils.mergeArrays(result, [this.getBackwardArticleContainerOnRailRecursively(ac)]);
      }
    });
    return result;
  }

  public containsArticleConnectedToMountingRail(
    containers: ArticleContainer[],
    rangeOnLineType: string = null,
    considerOnlyConfiguredArticles: boolean = true,
    considerRangeOnLineType: boolean = true
  ): boolean {
    if (ArrayUtils.isEmpty(containers)) {
      return false;
    }
    for (const container of containers) {
      const articleConnectedToMountingRail: boolean = this._articleConnectionUtils.isArticleConnectedToMountingRail(
        container,
        rangeOnLineType,
        considerOnlyConfiguredArticles,
        considerRangeOnLineType
      );
      if (articleConnectedToMountingRail) {
        return true;
      }
    }
    return false;
  }

  public isAllArticlesConnectedToSameRangeOnLineContainer(articleContainers: ArticleContainer[]): boolean {
    if (ArrayUtils.isEmpty(articleContainers)) {
      return false;
    }
    let parentUsageId: string = null;
    for (const container of articleContainers) {
      if (!parentUsageId) {
        parentUsageId = ArticleUsageUtils.getFirstBackwardConnectedArticleUsageId(container.articleUsage);
      }
      if (!parentUsageId) {
        return false;
      }
      if (!this._articleConnectionUtils.isArticleConnectedToRangeOnLineArticleWithConfigured(container, parentUsageId)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Checks if the given article is connected to non-freedom accepting connection on mounting rail.
   * NOTE: Method does not consider articles connected to drilling holes as accessories of mounting rail.
   *
   * @param articleContainer article container that needs to be checked
   * @param mountingRailContainer optional parameter to specify mounting rail container if it was determined beforehand
   *
   * @return true if given article container is accessory of mounting rail, otherwise false
   */
  public isMountingRailAccessoryArticle(articleContainer: ArticleContainer, mountingRailContainer: ArticleContainer = null): boolean {
    if (!articleContainer) {
      return false;
    }
    const backwardConnectionId: MetaElementUsageIdentityVO = ArticleUsageUtils.getFirstBackwardConnectedConnectionUsageId(
      articleContainer.articleUsage
    );
    if (!backwardConnectionId) {
      return false;
    }
    if (mountingRailContainer && mountingRailContainer.articleGUID !== backwardConnectionId.articleUsageId) {
      return false;
    }
    if (!mountingRailContainer) {
      mountingRailContainer = this.containerService.findArticleContainerInGlobal(backwardConnectionId.articleUsageId);
    }
    if (!mountingRailContainer) {
      return false;
    }
    if (!ArticleUtils.isMountingRail(mountingRailContainer.article)) {
      return false;
    }
    const acceptingArea: MetaElementVO = mountingRailContainer.findMetaElementByName(backwardConnectionId.metaElementName);
    return acceptingArea == null ? false : !ConnectionInfoUtils.isRangeOnLineConnection(acceptingArea);
  }

  public filterArticlesConnectedToMountingRail(articleContainers: ArticleContainer[], considerCompositeParts: boolean = false): ArticleContainer[] {
    const articlesOnMountingRail: ArticleContainer[] = [];
    const processedGuids: string[] = [];
    for (const container of articleContainers) {
      if (this.compositeArticleInfoUtils.isCompositePartArticle(container) && considerCompositeParts) {
        if (processedGuids.indexOf(container.articleGUID) !== -1) {
          continue;
        }

        const compositeParts: ArticleContainer[] = this.containerService.getCompositePartArticles(container);
        for (const partArticle of compositeParts) {
          if (processedGuids.indexOf(partArticle.articleGUID) !== -1) {
            continue;
          }

          processedGuids.push(partArticle.articleGUID);
          if (this._articleConnectionUtils.isArticleConnectedToMountingRail(partArticle)) {
            articlesOnMountingRail.push(partArticle);
          }
        }
      } else if (this._articleConnectionUtils.isArticleConnectedToMountingRail(container)) {
        articlesOnMountingRail.push(container);
      }
    }
    return articlesOnMountingRail;
  }

  public findMountingRailToDrillingHole(container: ArticleContainer): ArticleContainer {
    if (!container) {
      return null;
    }
    const parentConnectionUsageIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(container.articleUsage);
    if (parentConnectionUsageIds.length !== 1) {
      return null;
    }
    const parentConnectionId: MetaElementUsageIdentityVO = parentConnectionUsageIds[0];
    const parentContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(parentConnectionId.articleUsageId);
    if (!parentContainer) {
      return null;
    }
    if (ArticleUtils.isMountingRail(parentContainer.article)) {
      return parentContainer;
    } else {
      return null;
    }
  }

  public static isSelfConnectingBridgesAllowed(acceptingContainer: ArticleContainer): boolean {
    if (!acceptingContainer || !acceptingContainer.article) {
      return false;
    }
    return ArticleUtils.getArticleBehaviorPropertyBooleanValue(
      acceptingContainer.article,
      ArticleBehaviorPropertyDef.IS_SELF_CONNECTING_BRIDGES_ALLOWED
    );
  }

  /**
   * Method calculates rectangle for giving connections. If inverse is set to false then line between point1 and point2
   * defines "U" direction. Otherwise its "V" direction.
   * Given connection must be sorted because first connection from given array will be used as closest connection,
   * last connection - as farthest.
   *
   * @param sortedConnections sorted connection cubes
   * @param inverse if the rectangle should be created for accepting connections then flag must be set to false (if giving - then true)
   * @return bounding rectangle for selected connections
   *
   */
  public calculateBridgeRectangle(sortedConnections: Connection[], inverse: boolean = false): Rectangle3D {
    if (sortedConnections.length === 1) {
      return sortedConnections[0].compositionRectangle;
    }
    const closestConnectionCube: Connection = sortedConnections[0];
    const farestConnectionCube: Connection = sortedConnections[sortedConnections.length - 1];
    return ConnectionInfoUtils.calculateCommonRectangle(
      [closestConnectionCube.compositionRectangle, farestConnectionCube.compositionRectangle],
      inverse
    );
  }

  public static isStaggeredJumperArticleContainers(articleContainers: ArticleContainer[]): boolean {
    if (!articleContainers || articleContainers.length === 0) {
      return false;
    }
    for (const articleContainer of articleContainers) {
      if (!ArticleUtils.isStaggeredJumperArticle(articleContainer.article)) {
        return false;
      }
    }
    return true;
  }

  public hasConnectedStaggeredJumper(acceptingConnection: Connection): boolean {
    if (!acceptingConnection) {
      return false;
    }
    const connectionAreaUsage: MetaElementUsageVO = acceptingConnection.metaElementUsage;
    const connectedArticleUsageIds: string[] = MetaElementUsageUtils.forwardConnectedArticleUsageIds(connectionAreaUsage);
    if (connectedArticleUsageIds.length === 0) {
      return false;
    }
    for (const usageId of connectedArticleUsageIds) {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);
      if (articleContainer && ArticleUtils.isStaggeredJumperArticle(articleContainer.article)) {
        return true;
      }
    }
    return false;
  }

  public static isLeftSideOpen(container: ArticleContainer): boolean {
    if (!container || !container.article || !container.articleUsage) {
      return false;
    }
    const appliedRotationAngle: number = ArticleUsageUtils.getArticleUsagePlausiPropertyIntValue(
      container.articleUsage,
      ArticleUsagePlausiPropertyDef.APPLIED_ROTATION_ANGLE
    );
    const isArticleRotated: boolean = NumberUtils.isNumber(appliedRotationAngle) && appliedRotationAngle !== 0;
    return PropertyUtils.getBooleanPropertyValue(
      container.article.plausiProperties,
      isArticleRotated ? ArticlePlausiPropertyDef.IS_RIGHT_SIDE_OPEN : ArticlePlausiPropertyDef.IS_LEFT_SIDE_OPEN
    );
  }

  public static isRightSideOpen(container: ArticleContainer | undefined): boolean {
    if (!container || !container.article || !container.articleUsage) {
      return false;
    }
    const appliedRotationAngle: number = ArticleUsageUtils.getArticleUsagePlausiPropertyIntValue(
      container.articleUsage,
      ArticleUsagePlausiPropertyDef.APPLIED_ROTATION_ANGLE
    );
    const isArticleRotated: boolean = NumberUtils.isNumber(appliedRotationAngle) && appliedRotationAngle !== 0;
    return PropertyUtils.getBooleanPropertyValue(
      container.article.plausiProperties,
      isArticleRotated ? ArticlePlausiPropertyDef.IS_LEFT_SIDE_OPEN : ArticlePlausiPropertyDef.IS_RIGHT_SIDE_OPEN
    );
  }

  /**
   * Checks if copy-operation is available for the given articles.
   * Copy is allowed when given list of articles contains articles which are connected to range-on-line article (e.g. mounting-rail, busbar)
   * and all these articles connected to the same range-on-line article.
   *
   * Articles which are not connected to range-on-line article (e.g. accessories) are ignored.
   */
  public isCopyArticlesAvailable(articleContainers: ArticleContainer[]): boolean {
    if (!articleContainers) {
      return false;
    }

    let parentUsageId: string = null;
    for (const container of articleContainers) {
      if (!this._articleConnectionUtils.isArticleConnectedToRangeOnLineArticleWithConfigured(container)) {
        continue;
      }

      if (
        container.projectModel.currentProjectType == "FREE_MOUNTING" &&
        !ArticleUtils.canAddArticleToFreeMounting(container.projectModel.currentProject, container.projectModel.user , container.article)
      ) {
        return false;
      }

      const containerBackwardUsageId: string = ArticleUsageUtils.getFirstBackwardConnectedArticleUsageId(container.articleUsage);
      if (!parentUsageId) {
        parentUsageId = containerBackwardUsageId;
      } else {
        if (parentUsageId !== containerBackwardUsageId) {
          return false;
        }
      }
    }
    return !!parentUsageId;
  }

  public getArticleTypeByArticleUsageId(articleUsageId: string): string {
    if (!articleUsageId) {
      return null;
    }
    const container = this.containerService.findArticleContainerInGlobal(articleUsageId);
    if (!container || !container.article || !container.article.type) {
      return null;
    }
    return container.article.type.displayName;
  }

  public isAllArticlesConnectedToSameRangeOnLineArticleType(articleContainers: ArticleContainer[]): boolean {
    if (ArrayUtils.isEmpty(articleContainers)) {
      return false;
    }
    let parentArticleType: string = null;

    for (const container of articleContainers) {
      if (!parentArticleType) {
        const parentUsageId: string = ArticleUsageUtils.getFirstBackwardConnectedArticleUsageId(container.articleUsage);
        parentArticleType = this.getArticleTypeByArticleUsageId(parentUsageId);
      }
      if (!parentArticleType) {
        return false;
      }
      if (!this._articleConnectionUtils.isArticleConnectedToRangeOnLineArticleType(container, parentArticleType)) {
        return false;
      }
    }
    return true;
  }

  public isRecoverBridgePinsAvailable(container: ArticleContainer): boolean {
    if (!container) {
      return false;
    }
    const isBridge = ArticleUtils.isBridge(container.article);
    const hasFreeConnections = ArrayUtils.isNotEmpty(ConnectionInfoUtils.getFreeConnectionAreas(container, ConnectionAreaVO.SIDE_GIVING));
    return isBridge && hasFreeConnections;
  }

  public isBusbarCarrierArticle(articleContainer: ArticleContainer): boolean {
    const article: ArticleVO = articleContainer.article;
    return (
      (ArticleUtils.isEndStopArticle(article) || ArticleUtils.isTerminalBlockArticle(article)) &&
      !!ConnectionInfoUtils.getBusbarConnections(articleContainer).length
    );
  }

  public static getDynamicArticleStretchLength(container: ArticleContainer): number {
    const propertyValue: any = container.properties.getValue(ArticleContainerPropertyDef.STRETCH_LENGTH);
    if (!propertyValue) {
      return NaN;
    }
    if (propertyValue instanceof Array) {
      return ArrayUtils.isNotEmpty(propertyValue) ? Number(propertyValue[0]) : NaN;
    } else {
      return Number(propertyValue);
    }
  }

  /** @returns true when the articleContainer itself or its CompositeArticle is an McsGdsLtlArticle */
  public isOrIsPartOfMcsGdsLtl(container: ArticleContainer) {
    let isArticleMcsGdsLtl = ArticleUtils.isMcsGdsLtlProjectArticle(container.article);

    if(isArticleMcsGdsLtl) {
      return true;
    }

    const compositeArticle = this.compositeArticleInfoUtils.getCompositeArticleFromContainer(container);

    if(compositeArticle) {
      isArticleMcsGdsLtl = ArticleUtils.isMcsGdsLtlProjectArticle(compositeArticle);
    }

    return isArticleMcsGdsLtl;
  }

  public static copyColorValuesToInternalConnection(selectedConnection: Connection, container: ArticleContainer) {
    const selectedName = selectedConnection.connectionName;
    const targetName = selectedName.endsWith('_internal')
      ? selectedName.slice(0, -'_internal'.length)
      : selectedName + '_internal';
    for (let connection of container.connections) {
      if (connection.connectionName === targetName) {
        connection.setDefaultFillColor(selectedConnection.getDefaultFillColor());
        connection.setDefaultOpacity(selectedConnection.getDefaultOpacity());
      }
    }
  }

  public static getTextureConnections(articleContainers: ArticleContainer[]) {
    return articleContainers.flatMap((articleContainer) => articleContainer.connections).filter((connection) => connection.isTextureArea());
  }
}
